// miscBespokeTools.js — AXIS1/2, POLYLINE1, CORRIDOR1, RECTANGULAR1, POINT12

import { DrawRules, Modifiers } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import { Point, LineString } from 'ol/geom';
import { toLonLat } from 'ol/proj';
import { registerDrawInteraction } from '../customDrawInteractions';
import { createFixedPointCollector } from './fixedPointCollector';
import { createSequentialCollector } from './sequentialCollector';
import { startLivePreview } from './livePreview';
import { PREVIEW_DOT_BLUE, PREVIEW_DOT_AMBER, PREVIEW_LINE_BLUE, PREVIEW_LINE_AMBER_DASHED } from './drawPreviewStyles';

// Default corridor half-width when no explicit width point is given (metres).
const DEFAULT_CORRIDOR_HALF_WIDTH_M = 500;

// ── AXIS1 and AXIS2: N-point spine, then one width point ──────────────────────
// Phase 1: free polyline (double-click to end spine at ≥2 pts)
// Phase 2: one more click = width point, passed through as a literal PN coordinate (see
// finish() below for why this can't be an AM_DISTANCE modifier for this DrawRule family).
//
// Ticket 04: kept as its own bespoke two-phase state machine rather than rebuilt on top of
// multiPointCollector.js — that shared base models a single click-count range with one
// finish, not "collect a variable-length run, then downgrade to collecting exactly one more
// point of a different role", and forcing this collector's delicate ticket-01 reversal logic
// (see finish() below) through an abstraction built for a different shape risked reintroducing
// the exact bug ticket 01 fixed. Escape-to-cancel and Backspace-to-undo-last-point (this
// file's own gap the ticket flagged) were added directly to the existing keydown handler
// instead, so the OBSERVABLE keyboard conventions still match every other migrated tool.
function makeAxisCollector(map, opts) {
    const onFinish = opts.onFinish;
    const sidc = opts.sidc || null;

    let collected = [];    // [[lng,lat], ...]
    let collected3857 = [];
    let phase = 1; // 1 = spine, 2 = width point

    const DOT = PREVIEW_DOT_BLUE;
    const SPINE = PREVIEW_LINE_BLUE;
    const WIDTH = PREVIEW_LINE_AMBER_DASHED;
    const WIDTH_DOT = PREVIEW_DOT_AMBER;

    const previewSource = new VectorSource();
    const previewLayer = new VectorLayer({ source: previewSource, zIndex: 10,
        style: (f) => {
            const t = f.get('_t');
            if (t === 'dot') return DOT;
            if (t === 'wdot') return WIDTH_DOT;
            if (t === 'width') return WIDTH;
            return SPINE;
        },
    });
    map.addLayer(previewLayer);

    // Live mil-sym preview (chevron/arrow shape shown during spine placement AND width
    // placement). The renderer's P1=tip/P(N-1)=rear/PN=width contract means finish() reverses
    // the clicked spine (see below) — the preview must track the SAME reversed order at every
    // phase, or the live chevron visibly points at the user's first click (rear) the entire
    // time they're drawing even though the final committed shape ends up correctly oriented:
    //   - getCollected always returns the spine reversed (matches finish()'s reversal).
    //   - phase 1 (still placing spine): the live cursor is a TENTATIVE NEXT spine point,
    //     which would become the new P1/tip if finalized now — so it must render BEFORE the
    //     already-reversed spine (cursorFirst: true).
    //   - phase 2 (placing width point): the spine is already final or reverse(collected) is
    //     the finished P1..P(N-1) order; the cursor is the tentative width point PN, which
    //     comes AFTER the spine, not before (cursorFirst: false).
    let stopLive = null;
    if (sidc) {
        stopLive = startLivePreview(map, sidc, () => [...collected].reverse(), 2, { cursorFirst: () => phase === 1 });
    }

    function updateSketch(cursorCoord) {
        previewSource.clear(true);
        const spine3857 = phase === 2
            ? collected3857.slice(0, collected3857.length - 0) // all spine points during width phase
            : collected3857;
        spine3857.forEach((c) => { const f = new Feature(new Point(c)); f.set('_t', 'dot'); previewSource.addFeature(f); });
        if (spine3857.length >= 2) {
            const pts = cursorCoord && phase === 1 ? [...spine3857, cursorCoord] : spine3857;
            const lf = new Feature(new LineString(pts));
            lf.set('_t', 'spine');
            previewSource.addFeature(lf);
        }
        if (phase === 2) {
            if (cursorCoord) {
                const wf = new Feature(new Point(cursorCoord));
                wf.set('_t', 'wdot');
                previewSource.addFeature(wf);
                // Width bracket from spine endpoint to width point
                const last = spine3857[spine3857.length - 1];
                const lineF = new Feature(new LineString([last, cursorCoord]));
                lineF.set('_t', 'width');
                previewSource.addFeature(lineF);
            }
        }
    }

    const onPointerMove = (e) => updateSketch(e.coordinate);

    const onClick = (e) => {
        if (e.originalEvent.button !== 0) return;
        const lonLat = toLonLat(e.coordinate);
        collected.push([lonLat[0], lonLat[1]]);
        collected3857.push(e.coordinate);
        if (phase === 2) {
            updateSketch(null);
            finish();
        } else {
            updateSketch(null);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && phase === 1 && collected.length >= 2) {
            phase = 2;
            return;
        }
        if (e.key === 'Escape') { cleanup(); return; } // cancel — no onFinish
        if (e.key === 'Backspace' && collected.length > 0) {
            collected.pop();
            collected3857.pop();
            // Undoing the spine's 2nd-to-last point can legitimately drop below 2 while still
            // in phase 1 — nothing else to adjust, phase only ever advances forward via
            // Enter/double-click, never automatically backward.
            updateSketch(null);
        }
    };

    const onDblClick = (e) => {
        if (phase === 1 && collected.length >= 2) { e.preventDefault(); phase = 2; }
    };

    function finish() {
        cleanup();
        if (collected.length < 3) {
            // Not enough points for a valid axis; abort silently
            return;
        }
        // Split: spine = all but last (clicked in spine phase), widthPt = last (phase-2 click).
        // AXIS1/AXIS2's DrawRule contract is P1 = arrowhead tip, P(N-1) = rear, PN = width point
        // (see drawRuleMeta.js / the real library's DrawRules.d.mts JSDoc — confirmed empirically
        // against the actual mil-sym-ts-web WebRenderer, not just the doc comment). The natural
        // click gesture is "click my own position (rear) first, then click toward the target
        // (tip) last" — the OPPOSITE order — so the clicked SPINE is reversed here so P1 becomes
        // the user's last-clicked (target) point and the last spine entry becomes their
        // first-clicked (rear) point; without this the rendered arrow points back at the user's
        // start instead of at their target.
        //
        // The width point (PN) is passed through to the renderer as a LITERAL coordinate, not
        // converted to an AM_DISTANCE modifier: MSInfo.getMinPointCount() for every AXIS1/AXIS2
        // SIDC is 3, and the real WebRenderer.RenderSymbol2D throws ("requires a minimum of 3
        // points") when given only the 2-point spine + an AM_DISTANCE modifier — it does not
        // accept AM_DISTANCE as a substitute for the missing literal width point for this
        // DrawRule family (unlike CORRIDOR1 below, whose MSInfo min is 2, so a spine-only +
        // AM_DISTANCE default width legitimately works there). Confirmed empirically by
        // rendering both ways against the real library.
        const widthPt = collected[collected.length - 1];
        const spineCoords = collected.slice(0, collected.length - 1).reverse();
        onFinish([...spineCoords, widthPt]);
    }

    function cleanup() {
        map.un('click', onClick);
        map.un('pointermove', onPointerMove);
        document.removeEventListener('keydown', onKeyDown);
        map.getViewport().removeEventListener('dblclick', onDblClick);
        map.removeLayer(previewLayer);
        if (stopLive) { stopLive(); stopLive = null; }
    }

    map.on('click', onClick);
    map.on('pointermove', onPointerMove);
    document.addEventListener('keydown', onKeyDown);
    map.getViewport().addEventListener('dblclick', onDblClick);

    return { interaction: null, getPlacedCount: () => collected.length, destroy: cleanup };
}

registerDrawInteraction(DrawRules.AXIS1, (map, opts) => makeAxisCollector(map, opts));
registerDrawInteraction(DrawRules.AXIS2, (map, opts) => makeAxisCollector(map, opts));

// ── POLYLINE1: 3-pt spine (P1, P2) + width side point (P3) ────────────────────
// P3 is not part of the spine direction, but MSInfo.getMinPointCount() for POLYLINE1 is
// exactly 3 — like AXIS1/2 above, the real renderer needs P3 passed through as a literal
// coordinate (it computes the perpendicular width itself); it does not accept an AM_DISTANCE
// substitute for the missing 3rd point. Pass all 3 collected points straight through.
// Ticket 22: sequential clicks + mil-sym live preview (was single-click spread).
registerDrawInteraction(DrawRules.POLYLINE1, (map, opts) => createSequentialCollector(map, {
    minCount: 3,
    maxCount: 3,
    onFinish: opts.onFinish,
    sidc: opts.sidc || null,
    previewMinPoints: 2,
    cursorFirst: false,
    roleForIndex: (i) => (i < 2 ? 'anchor' : 'width'),
    styleResolver: () => PREVIEW_LINE_BLUE,
    onSketchUpdate: ({ previewSource, collected3857, cursorCoord3857 }) => {
        const pts = cursorCoord3857 ? [...collected3857, cursorCoord3857] : collected3857;
        if (pts.length >= 2) {
            const lf = new Feature(new LineString(pts.slice(0, 2)));
            lf.set('_t', 'spine');
            previewSource.addFeature(lf);
        }
        if (collected3857.length >= 2 && cursorCoord3857) {
            const last = collected3857[1];
            const wf = new Feature(new LineString([last, cursorCoord3857]));
            wf.set('_t', 'width');
            previewSource.addFeature(wf);
        }
    },
}));

// ── CORRIDOR1: multi-waypoint spine + default half-width 500 m ─────────────────
// Phase: free polyline (double-click or Enter to finish at ≥2 pts).
// Width is stored as AM_DISTANCE; user may edit it post-placement via properties panel.
function makeCorridorCollector(map, opts) {
    return createSequentialCollector(map, {
        minCount: 2,
        maxCount: Infinity,
        onFinish: (coords) => {
            opts.onFinish([...coords], { sidcModifiers: { [Modifiers.AM_DISTANCE]: String(DEFAULT_CORRIDOR_HALF_WIDTH_M) } });
        },
        sidc: opts.sidc || null,
        // Preserve prior preview gate (was startLivePreview minPoints=2, not default minCount-1).
        previewMinPoints: 2,
        cursorFirst: false,
        // Every spine waypoint is an anchor — CORRIDOR1 has no directional tip/rear or width
        // point of its own (the half-width is a fixed default, not a clicked point).
        roleForIndex: () => 'anchor',
        styleResolver: () => PREVIEW_LINE_BLUE,
        onSketchUpdate: ({ previewSource, collected3857, cursorCoord3857 }) => {
            const pts = cursorCoord3857 ? [...collected3857, cursorCoord3857] : collected3857;
            if (pts.length >= 2) {
                const lf = new Feature(new LineString(pts));
                lf.set('_t', 'line');
                previewSource.addFeature(lf);
            }
        },
    });
}

registerDrawInteraction(DrawRules.CORRIDOR1, (map, opts) => makeCorridorCollector(map, opts));

// ── RECTANGULAR1: 2-pt centreline (width from AM in properties panel) ─────────
registerDrawInteraction(DrawRules.RECTANGULAR1, (map, opts) =>
    createFixedPointCollector(map, { targetCount: 2, onFinish: opts.onFinish }));

// ── POINT12: 3-pt (arrowhead tip 1, arrowhead tip 2, rear anchor) ─────────────
registerDrawInteraction(DrawRules.POINT12, (map, opts) =>
    createFixedPointCollector(map, { targetCount: 3, onFinish: opts.onFinish }));
